import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import styles from '~/modules/GameEngine/Drawing/styles';
import PlayerChange from '~/routes/RemoteMic/Panels/Microphone/PlayerChange';
import usePermissions from '~/routes/RemoteMic/hooks/usePermissions';

interface Props {
  volume: number;
  playerNumber: 0 | 1 | 2 | 3 | null;
  frequency: number | null;
  isMicOn: boolean;
  isConnected: boolean;
  showPlayerChangeModal?: boolean;
}

export default function VolumeIndicator({
  playerNumber,
  volume,
  frequency,
  isMicOn,
  isConnected,
  showPlayerChangeModal,
}: Props) {
  const [maxVolume, setMaxVolume] = useState(0.000001);
  const permissions = usePermissions();

  useEffect(() => {
    setMaxVolume((current) => (volume > current ? volume : current * 0.99));
  }, [volume]);

  const backgroundColor =
    playerNumber !== null ? styles.colors.players[playerNumber].hit.fill : styles.colors.lines.normal.fill;

  return (
    <IndicatorContainer
      className="rounded-md"
      data-is-mic-on={isMicOn}
      color={backgroundColor}
      data-player-number={`${playerNumber ?? 'none'}`}
      data-test="indicator">
      <MeterHeader>
        <span>{isMicOn ? 'Microphone live' : 'Microphone paused'}</span>
        <Debug>{frequency ? `${Math.round(frequency)}Hz` : ' '}</Debug>
      </MeterHeader>
      {isConnected && permissions === 'write' && (
        <PlayerChange playerNumber={playerNumber} defaultOpen={showPlayerChangeModal} />
      )}
      <Indicator style={{ transform: `scaleY(${isMicOn ? 1 - Math.min(1, volume / maxVolume) : 1})` }} />
    </IndicatorContainer>
  );
}

const MeterHeader = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 900;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
`;

const Debug = styled.span`
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  font-weight: 800;
`;

const Indicator = styled.div`
  width: 100%;
  min-height: 100%;
  background: linear-gradient(180deg, rgba(2, 7, 16, 0.82), rgba(2, 7, 16, 0.52));
  transition: 180ms ease-out;
  transform-origin: top;
`;

const IndicatorContainer = styled.div<{ color: string }>`
  position: relative;
  flex: 1;
  min-height: 180px;
  max-height: 280px;
  overflow: hidden;
  border: 1px solid var(--walky-border);
  border-radius: 16px;
  background: ${(props) => props.color};
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    var(--walky-shadow);
  transition: 200ms ease;

  &[data-is-mic-on='false'] {
    filter: saturate(0.55);
  }
`;
